import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  LinearProgress,
  Tooltip,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import PersonIcon from '@material-ui/icons/Person';
import { InfoCard, EmptyState } from '@backstage/core-components';
import type { DevxpApi } from '../api';
import type { LeaderboardDeveloper, LeaderboardFilters, DeveloperMapping } from '../types';

const CATEGORIES = [
  'Frontend Engineering',
  'Backend Systems',
  'Database & Data',
  'Infrastructure (DevOps)',
  'Mobile Development',
  'AI & Machine Learning',
  'Testing & QA',
  'Security & Auth',
  'Distributed Systems',
  'Documentation',
];

const CATEGORY_COLORS: Record<string, string> = {
  'Frontend Engineering': '#e91e63',
  'Backend Systems': '#3f51b5',
  'Database & Data': '#9c27b0',
  'Infrastructure (DevOps)': '#ff9800',
  'Mobile Development': '#4caf50',
  'AI & Machine Learning': '#607d8b',
  'Testing & QA': '#00bcd4',
  'Security & Auth': '#f44336',
  'Distributed Systems': '#795548',
  'Documentation': '#9e9e9e',
};

const useStyles = makeStyles(theme => ({
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'flex-end',
  },
  searchField: {
    minWidth: 200,
    flex: 1,
  },
  repoField: {
    minWidth: 240,
    flex: 2,
  },
  selectField: {
    minWidth: 160,
  },
  categorySelect: {
    minWidth: 200,
    flex: 1,
  },
  rankCell: {
    fontWeight: 700,
    color: theme.palette.text.secondary,
    width: 48,
  },
  userIdCell: {
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  scoreBar: {
    height: 6,
    borderRadius: 3,
    marginTop: 4,
  },
  categoryChip: {
    margin: theme.spacing(0.25),
    fontSize: '0.65rem',
    height: 18,
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    marginTop: theme.spacing(1),
  },
  medalGold: { color: '#FFD700', fontWeight: 700 },
  medalSilver: { color: '#C0C0C0', fontWeight: 700 },
  medalBronze: { color: '#CD7F32', fontWeight: 700 },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
}));

interface DeveloperLeaderboardProps {
  api: DevxpApi;
  onViewDeveloper: (userId: string) => void;
  onCompareDeveloper: (userId: string) => void;
  compareList: string[];
  /** Pre-loaded repo names from the parent. When provided the component skips its own fetch. */
  repoOptions?: string[];
}

export const DeveloperLeaderboard = ({
  api,
  onViewDeveloper,
  onCompareDeveloper,
  compareList,
  repoOptions: repoOptionsProp,
}: DeveloperLeaderboardProps) => {
  const classes = useStyles();

  const [developers, setDevelopers] = useState<LeaderboardDeveloper[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [devNames, setDevNames] = useState<Map<string, string>>(new Map());

  // Use parent-provided options when available, otherwise fetch own
  const [ownRepoOptions, setOwnRepoOptions] = useState<string[]>([]);
  const repoOptions = repoOptionsProp ?? ownRepoOptions;

  const [filters, setFilters] = useState<LeaderboardFilters>({
    page: 1,
    pageSize: 20,
    categories: [],
    repoNames: [],
    sortBy: 'avgProficiency',
    sortOrder: 'desc',
    searchQuery: '',
  });
  const [searchInput, setSearchInput] = useState('');

  // Only fetch repo names ourselves if the parent didn't supply them
  useEffect(() => {
    if (repoOptionsProp !== undefined) return;
    api
      .getRepositories({ pageSize: 200 })
      .then(data => setOwnRepoOptions(data.repositories.map(r => r.repoName)))
      .catch(() => {});
  }, [api, repoOptionsProp]);

  // Load all developer name mappings once to resolve masked IDs to real names
  useEffect(() => {
    api
      .getMappings()
      .then(({ mappings }: { mappings: DeveloperMapping[] }) => {
        const m = new Map<string, string>();
        mappings.forEach(dm => m.set(dm.masked_name, dm.real_name));
        setDevNames(m);
      })
      .catch(() => {});
  }, [api]);

  const load = useCallback(
    async (f: LeaderboardFilters) => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getDeveloperLeaderboard(f);
        setDevelopers(data.developers);
        setTotalCount(data.totalCount);
        setTotalPages(data.totalPages);
      } catch (e: any) {
        setError(e.message ?? 'Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    load(filters);
  }, [load, filters]);

  const applySearch = () => {
    setFilters(f => ({ ...f, page: 1, searchQuery: searchInput }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') applySearch();
  };

  const toggleSort = (field: 'avgProficiency' | 'domainSkillsScore') => {
    setFilters(f => ({
      ...f,
      page: 1,
      sortBy: field,
      sortOrder: f.sortBy === field && f.sortOrder === 'desc' ? 'asc' : 'desc',
    }));
  };

  const SortIcon = ({ field }: { field: 'avgProficiency' | 'domainSkillsScore' }) => {
    if (filters.sortBy !== field) return null;
    return filters.sortOrder === 'desc' ? (
      <ArrowDownwardIcon style={{ fontSize: 14, verticalAlign: 'middle' }} />
    ) : (
      <ArrowUpwardIcon style={{ fontSize: 14, verticalAlign: 'middle' }} />
    );
  };

  const rankLabel = (rank: number) => {
    if (rank === 1) return <span className={classes.medalGold}>🥇 1</span>;
    if (rank === 2) return <span className={classes.medalSilver}>🥈 2</span>;
    if (rank === 3) return <span className={classes.medalBronze}>🥉 3</span>;
    return <span>{rank}</span>;
  };

  const globalRank = (idx: number) => (filters.page - 1) * filters.pageSize + idx + 1;

  return (
    <InfoCard title={`Developer Leaderboard (${totalCount})`} noPadding={false}>
      {/* Filters */}
      <Box className={classes.filters}>
        <TextField
          className={classes.searchField}
          size="small"
          label="Search developer"
          variant="outlined"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <IconButton size="small" onClick={applySearch}>
                <SearchIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />
        <Autocomplete
          className={classes.repoField}
          multiple
          options={repoOptions}
          value={filters.repoNames}
          onChange={(_e, value) =>
            setFilters(f => ({ ...f, page: 1, repoNames: value }))
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option}
                label={option.split('/').pop() ?? option}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              label="Repositories"
              placeholder={filters.repoNames.length === 0 ? 'Filter by repo…' : ''}
            />
          )}
        />
        <FormControl variant="outlined" size="small" className={classes.categorySelect}>
          <InputLabel>Expertise domain</InputLabel>
          <Select
            multiple
            value={filters.categories}
            onChange={e =>
              setFilters(f => ({ ...f, page: 1, categories: e.target.value as string[] }))
            }
            label="Expertise domain"
            renderValue={(selected: any) =>
              (selected as string[])
                .map(s => s.split(' ')[0])
                .join(', ')
            }
          >
            {CATEGORIES.map(cat => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.selectField}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={filters.sortBy}
            onChange={e =>
              setFilters(f => ({ ...f, page: 1, sortBy: e.target.value as any }))
            }
            label="Sort by"
          >
            <MenuItem value="avgProficiency">Avg Proficiency</MenuItem>
            <MenuItem value="domainSkillsScore">Domain Score</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <EmptyState
          missing="data"
          title="Leaderboard unavailable"
          description={error}
        />
      ) : developers.length === 0 ? (
        <EmptyState
          missing="data"
          title="No developers found"
          description="Try adjusting the filters."
        />
      ) : (
        <React.Fragment>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.rankCell}>#</TableCell>
                  <TableCell>Developer</TableCell>
                  <TableCell>
                    <span
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleSort('avgProficiency')}
                    >
                      Avg Proficiency <SortIcon field="avgProficiency" />
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleSort('domainSkillsScore')}
                    >
                      Domain Score <SortIcon field="domainSkillsScore" />
                    </span>
                  </TableCell>
                  <TableCell>Skills</TableCell>
                  <TableCell>Top Domains</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {developers.map((dev, idx) => (
                  <TableRow
                    key={dev.userId}
                    className={classes.clickableRow}
                    onClick={() => onViewDeveloper(dev.userId)}
                  >
                    <TableCell className={classes.rankCell}>
                      {rankLabel(globalRank(idx))}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" className={classes.userIdCell}>
                        {devNames.get(dev.userId) ?? dev.userId}
                      </Typography>
                    </TableCell>
                    <TableCell style={{ minWidth: 120 }}>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {(dev.averageProficiency * 10).toFixed(1)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={dev.averageProficiency * 10}
                        className={classes.scoreBar}
                        style={{ color: '#3f51b5' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {dev.sumOfAveragedSkillsProficiency.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{dev.skillCount}</Typography>
                    </TableCell>
                    <TableCell style={{ maxWidth: 200 }}>
                      {dev.categoryBreakdown
                        .sort((a, b) => b.averageProficiency - a.averageProficiency)
                        .slice(0, 3)
                        .map(cat => (
                          <Chip
                            key={cat.category}
                            label={cat.category.split(' ')[0]}
                            size="small"
                            className={classes.categoryChip}
                            style={{
                              backgroundColor: `${CATEGORY_COLORS[cat.category] ?? '#9e9e9e'}20`,
                              color: CATEGORY_COLORS[cat.category] ?? '#9e9e9e',
                            }}
                          />
                        ))}
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <Box className={classes.actionButtons}>
                        <Tooltip title="View details">
                          <IconButton
                            size="small"
                            onClick={() => onViewDeveloper(dev.userId)}
                          >
                            <PersonIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            compareList.includes(dev.userId)
                              ? 'Added to comparison'
                              : 'Add to comparison'
                          }
                        >
                          <IconButton
                            size="small"
                            color={compareList.includes(dev.userId) ? 'primary' : 'default'}
                            onClick={() => onCompareDeveloper(dev.userId)}
                          >
                            <CompareArrowsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box className={classes.pagination}>
            <Typography variant="caption" color="textSecondary">
              Showing {(filters.page - 1) * filters.pageSize + 1}–
              {Math.min(filters.page * filters.pageSize, totalCount)} of {totalCount}
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Button
                size="small"
                disabled={filters.page <= 1}
                onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
              >
                Prev
              </Button>
              <Typography variant="caption">
                {filters.page} / {totalPages}
              </Typography>
              <Button
                size="small"
                disabled={filters.page >= totalPages}
                onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
              >
                Next
              </Button>
            </Box>
          </Box>
        </React.Fragment>
      )}
    </InfoCard>
  );
};
